import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './WhatWeDoModal.css';

function WhatWeDoModal({ isOpen, onClose, data, currentIndex, totalCount, onPrevious, onNext }) {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const [buttonPosition, setButtonPosition] = useState({ left: 0, right: 0 });
  const [shouldRender, setShouldRender] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      document.body.style.overflow = 'hidden';
      // 延迟添加打开类名，确保动画可以播放
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
    } else {
      setIsAnimating(false);
      // 关闭时延迟卸载，让关闭动画可以播放
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 300); // 与 CSS 过渡时间一致
      document.body.style.overflow = 'unset';
      return () => clearTimeout(timer);
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const calculateButtonPosition = () => {
      if (!containerRef.current) return;

      const viewportWidth = window.innerWidth;
      const containerWidth = containerRef.current.offsetWidth;
      
      // 根据屏幕尺寸确定按钮宽度
      let buttonWidth = 68; // 桌面端默认
      if (viewportWidth <= 833) {
        buttonWidth = 36; // 移动端
      } else if (viewportWidth <= 1199) {
        buttonWidth = 40; // 平板端
      }
      
      // 移动端：按钮放在弹窗内部
      if (viewportWidth <= 833) {
        setButtonPosition({
          left: 10,
          right: 10
        });
        return;
      }
      
      // 计算距离视窗边缘的距离：(视窗宽度 - 弹窗宽度) / 4
      const distanceFromViewport = (viewportWidth - containerWidth) / 4;
      
      // 按钮中心距离弹窗边缘的距离 = 距离视窗边缘的距离 - 按钮宽度的一半
      const buttonOffset = distanceFromViewport - buttonWidth / 2;
      
      setButtonPosition({
        left: -buttonOffset,
        right: -buttonOffset
      });
    };

    if (isOpen) {
      calculateButtonPosition();
      window.addEventListener('resize', calculateButtonPosition);
      // 使用 setTimeout 和 requestAnimationFrame 确保 DOM 已渲染
      setTimeout(() => {
        requestAnimationFrame(calculateButtonPosition);
      }, 0);
    }

    return () => {
      window.removeEventListener('resize', calculateButtonPosition);
    };
  }, [isOpen, data]);

  if (!shouldRender || !data) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      onPrevious();
    }
  };

  const handleNext = () => {
    if (currentIndex < totalCount - 1) {
      onNext();
    }
  };

  const showPreviousButton = currentIndex > 0;
  const showNextButton = currentIndex < totalCount - 1;

  return (
    <div className={`what-we-do-modal-overlay ${isAnimating ? 'what-we-do-modal-overlay-open' : ''}`} onClick={handleBackdropClick}>
      <div 
        className={`what-we-do-modal-container ${isAnimating ? 'what-we-do-modal-container-open' : ''}`} 
        ref={containerRef}
        style={{
          backgroundImage: 'url(/image/宗旨弹窗背景图.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <button className="what-we-do-modal-close" onClick={onClose} aria-label={t('common.ariaClose')}>
          ×
        </button>
        
        {showPreviousButton && (
          <button 
            className="what-we-do-modal-nav-button what-we-do-modal-nav-left"
            onClick={handlePrevious}
            aria-label={t('common.ariaPrev')}
            style={{ left: `${buttonPosition.left}px` }}
          >
            <span className="what-we-do-modal-nav-icon">←</span>
          </button>
        )}

        <div className="what-we-do-modal-content">
          <h2 className="what-we-do-modal-title">{data.subtitle}</h2>
          <p className="what-we-do-modal-intro">{t(`home.whatWeDo.${data.type}.modal.intro`)}</p>
          <ol className="what-we-do-modal-list">
            {data.details && data.details.map((item, index) => {
              // 检查是否是对象格式（包含title和items）
              if (typeof item === 'object' && item.title && item.items) {
                return (
                  <li key={index} className="what-we-do-modal-list-item what-we-do-modal-list-item-with-sub">
                    <div className="what-we-do-modal-list-item-main">
                      <span className="what-we-do-modal-list-number">{index + 1}</span>
                      <span className="what-we-do-modal-list-text what-we-do-modal-list-title">{item.title}</span>
                    </div>
                    <ul className="what-we-do-modal-sublist">
                      {item.items.map((subItem, subIndex) => (
                        <li key={subIndex} className="what-we-do-modal-sublist-item">
                          <span className="what-we-do-modal-sublist-bullet"></span>
                          <span className="what-we-do-modal-sublist-text">{subItem}</span>
                        </li>
                      ))}
                    </ul>
                  </li>
                );
              }
              // 普通字符串格式
              return (
                <li key={index} className="what-we-do-modal-list-item">
                  <span className="what-we-do-modal-list-number">{index + 1}</span>
                  <span className="what-we-do-modal-list-text">{item}</span>
                </li>
              );
            })}
          </ol>
        </div>

        {showNextButton && (
          <button 
            className="what-we-do-modal-nav-button what-we-do-modal-nav-right"
            onClick={handleNext}
            aria-label={t('common.ariaNext')}
            style={{ right: `${buttonPosition.right}px` }}
          >
            <span className="what-we-do-modal-nav-icon">→</span>
          </button>
        )}
      </div>
    </div>
  );
}

export default WhatWeDoModal;

